package org.zq.astrock_ai.ui.research.gushi.data

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import org.zq.astrock_ai.ui.research.gushi.StockColors

/**
 * 聊天关键信息着色：涨跌%、价格区间、买卖信号、风险词、六位代码等。
 */
fun highlightChatText(
    text: String,
    baseColor: Color,
): AnnotatedString {
    if (text.isEmpty()) return AnnotatedString("")

    data class Span(val start: Int, val end: Int, val style: SpanStyle)

    val spans = ArrayList<Span>()

    fun addAll(regex: Regex, style: SpanStyle) {
        regex.findAll(text).forEach { m ->
            spans += Span(m.range.first, m.range.last + 1, style)
        }
    }

    val priceStyle = SpanStyle(
        color = StockColors.PrimaryBlue,
        fontWeight = FontWeight.SemiBold,
    )
    val upStyle = SpanStyle(
        color = StockColors.UpRed,
        fontWeight = FontWeight.SemiBold,
    )
    val downStyle = SpanStyle(
        color = StockColors.DownGreen,
        fontWeight = FontWeight.SemiBold,
    )
    val riskStyle = SpanStyle(
        color = StockColors.Orange,
        fontWeight = FontWeight.SemiBold,
    )
    val codeStyle = SpanStyle(
        color = Color(0xFF0F766E),
        fontWeight = FontWeight.Medium,
    )

    // 涨跌幅（先匹配，避免被普通数字吃掉）
    addAll(Regex("""[＋+]\s*\d+(?:\.\d+)?\s*%"""), upStyle)
    addAll(Regex("""[－\-]\s*\d+(?:\.\d+)?\s*%"""), downStyle)
    addAll(Regex("""\d+(?:\.\d+)?\s*%"""), priceStyle)

    // 价格区间 / 价位
    addAll(Regex("""\d+\.\d{1,3}\s*[–—\-~至到]\s*\d+\.\d{1,3}"""), priceStyle)
    addAll(Regex("""(?:现价|成本|卖出|买入|支撑|压力|建议卖出|目标价)[：:\s]*\d+(?:\.\d{1,3})?"""), priceStyle)
    addAll(Regex("""\d+\.\d{2}(?:\s*元)?"""), priceStyle)

    // 六位股票代码
    addAll(Regex("""(?<!\d)\d{6}(?!\d)"""), codeStyle)

    // 关键词
    addAll(Regex("""买入|做多|偏多|看多|加仓|抄底"""), upStyle)
    addAll(Regex("""卖出|做空|偏空|看空|减仓|止盈|兑现"""), downStyle)
    addAll(Regex("""风险|止损|谨慎|观望|高危|回撤|预警"""), riskStyle)
    addAll(Regex("""建议卖出|卖区|压力带|支撑位"""), downStyle)

    // 合并重叠：后者优先（后添加的规则覆盖）
    val occupied = BooleanArray(text.length)
    val ordered = spans.sortedByDescending { it.end - it.start }
    val accepted = ArrayList<Span>()
    for (s in ordered) {
        if (s.start >= s.end || s.start < 0 || s.end > text.length) continue
        var clash = false
        for (i in s.start until s.end) {
            if (occupied[i]) {
                clash = true
                break
            }
        }
        if (clash) continue
        for (i in s.start until s.end) occupied[i] = true
        accepted += s
    }
    accepted.sortBy { it.start }

    return buildAnnotatedString {
        var cursor = 0
        for (s in accepted) {
            if (cursor < s.start) {
                withStyle(SpanStyle(color = baseColor)) {
                    append(text.substring(cursor, s.start))
                }
            }
            withStyle(s.style) {
                append(text.substring(s.start, s.end))
            }
            cursor = s.end
        }
        if (cursor < text.length) {
            withStyle(SpanStyle(color = baseColor)) {
                append(text.substring(cursor))
            }
        }
    }
}
